import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { Car, Play, Eye, Image, Video, Upload, Loader2, MessageCircle, Send, Trash2 } from "lucide-react";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { useAuth } from "../App";
import { toast } from "sonner";
import LikeButton from "../components/LikeButton";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GaragePage = () => {
  const { user } = useAuth();
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedType, setSelectedType] = useState("all");
  const [showUpload, setShowUpload] = useState(false);
  const [showSubmit, setShowSubmit] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadTitle, setUploadTitle] = useState("");
  const [uploadDesc, setUploadDesc] = useState("");
  const [uploadTags, setUploadTags] = useState("");
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [loadingComments, setLoadingComments] = useState(false);

  const getAuthHeaders = () => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchMedia = async () => {
    try {
      const response = await axios.get(`${API}/cars`);
      setMedia(response.data);
    } catch (error) {
      console.error("Error fetching car media:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchMedia(); }, []);

  const fetchComments = async (mediaId) => {
    setLoadingComments(true);
    try {
      const res = await axios.get(`${API}/cars/${mediaId}/comments`);
      setComments(res.data);
    } catch (e) { setComments([]); }
    finally { setLoadingComments(false); }
  };

  const handlePostComment = async () => {
    if (!newComment.trim() || !selectedMedia) return;
    if (!user) { toast.error("Please login to comment"); return; }
    try {
      const headers = getAuthHeaders();
      const res = await axios.post(`${API}/cars/${selectedMedia.media_id}/comments`, { content: newComment }, { headers, withCredentials: true });
      setComments(prev => [res.data, ...prev]);
      setNewComment("");
    } catch (e) { toast.error("Failed to post comment"); }
  };

  const handleDeleteComment = async (commentId) => {
    try {
      const headers = getAuthHeaders();
      await axios.delete(`${API}/cars/comments/${commentId}`, { headers, withCredentials: true });
      setComments(prev => prev.filter(c => c.comment_id !== commentId));
    } catch (e) { toast.error("Failed to delete"); }
  };

  const handleAdminUpload = async (e) => {
    e.preventDefault();
    if (!uploadFile) return;
    setUploading(true);
    try {
      const headers = getAuthHeaders();
      const formData = new FormData();
      formData.append("file", uploadFile);
      formData.append("title", uploadTitle);
      formData.append("description", uploadDesc);
      formData.append("tags", uploadTags);
      await axios.post(`${API}/admin/cars/upload`, formData, {
        headers: { ...headers, "Content-Type": "multipart/form-data" },
        withCredentials: true
      });
      toast.success("Media uploaded!");
      resetUploadForm();
      setShowUpload(false);
      fetchMedia();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload");
    } finally { setUploading(false); }
  };

  const handleUserSubmit = async (e) => {
    e.preventDefault();
    if (!uploadFile) return;
    setUploading(true);
    try {
      const headers = getAuthHeaders();
      const formData = new FormData();
      formData.append("file", uploadFile);
      formData.append("title", uploadTitle);
      formData.append("description", uploadDesc);
      formData.append("tags", uploadTags);
      await axios.post(`${API}/cars/submit`, formData, {
        headers: { ...headers, "Content-Type": "multipart/form-data" },
        withCredentials: true
      });
      toast.success("Submitted! Your content will be reviewed by the team.");
      resetUploadForm();
      setShowSubmit(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit");
    } finally { setUploading(false); }
  };

  const resetUploadForm = () => {
    setUploadFile(null);
    setUploadTitle("");
    setUploadDesc("");
    setUploadTags("");
  };

  const filteredMedia = media.filter(item =>
    selectedType === "all" || item.media_type === selectedType
  );

  const resolveUrl = (url) => {
    if (!url) return "";
    if (url.startsWith("http")) return url;
    return `${BACKEND_URL}${url}`;
  };

  const openMediaDetail = (item) => {
    setSelectedMedia(item);
    fetchComments(item.media_id);
  };

  const UploadForm = ({ onSubmit, submitLabel }) => (
    <form onSubmit={onSubmit} className="space-y-4">
      <div>
        <Label>File (Image or Video)</Label>
        <Input type="file" accept="image/*,video/*" onChange={(e) => setUploadFile(e.target.files[0])} className="bg-black/50 border-white/10" required data-testid="garage-file-input" />
        {uploadFile && <p className="text-xs text-zinc-500 mt-1">{uploadFile.name} ({(uploadFile.size / 1024 / 1024).toFixed(1)} MB)</p>}
      </div>
      <div>
        <Label>Title</Label>
        <Input value={uploadTitle} onChange={(e) => setUploadTitle(e.target.value)} className="bg-black/50 border-white/10" required placeholder="JDM Night Meet" data-testid="garage-upload-title" />
      </div>
      <div>
        <Label>Description</Label>
        <Textarea value={uploadDesc} onChange={(e) => setUploadDesc(e.target.value)} className="bg-black/50 border-white/10" placeholder="Describe the content..." />
      </div>
      <div>
        <Label>Tags (comma separated)</Label>
        <Input value={uploadTags} onChange={(e) => setUploadTags(e.target.value)} className="bg-black/50 border-white/10" placeholder="jdm, night, meet" />
      </div>
      <Button type="submit" className="w-full bg-neon-cyan text-black font-bold" disabled={uploading} data-testid="garage-submit-upload">
        {uploading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Uploading...</> : <><Upload className="w-4 h-4 mr-2" />{submitLabel}</>}
      </Button>
    </form>
  );

  return (
    <div className="min-h-screen py-10 px-6" data-testid="garage-page">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-10 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <Badge className="bg-neon-green/20 text-neon-green border-neon-green/50 mb-4">Car Culture</Badge>
            <h1 className="font-heading text-4xl md:text-6xl uppercase tracking-tight mb-4">The Garage</h1>
            <p className="text-zinc-400 max-w-2xl">JDM meets hip-hop. Car meet footage, custom builds, and automotive culture content.</p>
          </div>
          <div className="flex gap-2 self-start md:self-auto">
            {/* Admin Upload */}
            {user?.is_admin && (
              <Dialog open={showUpload} onOpenChange={setShowUpload}>
                <DialogTrigger asChild>
                  <Button className="bg-neon-cyan text-black font-bold" data-testid="garage-upload-button">
                    <Upload className="w-4 h-4 mr-2" />Upload
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-zinc-900 border-white/10 max-w-lg">
                  <DialogHeader><DialogTitle className="font-heading">Upload to Garage</DialogTitle></DialogHeader>
                  <UploadForm onSubmit={handleAdminUpload} submitLabel="Upload" />
                </DialogContent>
              </Dialog>
            )}
            {/* User Submit */}
            {user && !user.is_admin && (
              <Dialog open={showSubmit} onOpenChange={setShowSubmit}>
                <DialogTrigger asChild>
                  <Button variant="outline" className="border-neon-green text-neon-green" data-testid="garage-submit-button">
                    <Upload className="w-4 h-4 mr-2" />Submit Your Ride
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-zinc-900 border-white/10 max-w-lg">
                  <DialogHeader><DialogTitle className="font-heading">Submit Your Content</DialogTitle></DialogHeader>
                  <p className="text-sm text-zinc-500 mb-2">Submit your car photos/videos for review. Approved content will appear in the Garage!</p>
                  <UploadForm onSubmit={handleUserSubmit} submitLabel="Submit for Review" />
                </DialogContent>
              </Dialog>
            )}
          </div>
        </div>

        {/* Type Filter */}
        <div className="flex gap-2 mb-8">
          {[{ val: "all", label: "All", icon: null }, { val: "image", label: "Photos", icon: Image }, { val: "video", label: "Videos", icon: Video }].map(({ val, label, icon: Icon }) => (
            <Button key={val} variant={selectedType === val ? "default" : "outline"} onClick={() => setSelectedType(val)}
              className={selectedType === val ? "bg-neon-green text-black" : "border-white/20"}>
              {Icon && <Icon className="w-4 h-4 mr-2" />}{label}
            </Button>
          ))}
        </div>

        {/* Media Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => <div key={i} className="aspect-video bg-zinc-900/50 animate-pulse rounded-sm" />)}
          </div>
        ) : filteredMedia.length === 0 ? (
          <div className="text-center py-20">
            <Car className="w-16 h-16 text-zinc-600 mx-auto mb-4" />
            <p className="text-zinc-400 mb-2">No car content yet</p>
            <p className="text-zinc-600 text-sm">Check back soon for car meet footage and build showcases!</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredMedia.map((item, index) => (
              <motion.div key={item.media_id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }} data-testid={`car-media-${item.media_id}`}>
                <div className="group relative aspect-video overflow-hidden bg-zinc-900 border border-white/5 card-hover cursor-pointer"
                  onClick={() => openMediaDetail(item)}>
                  <img src={resolveUrl(item.thumbnail_url || item.media_url)} alt={item.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    onError={(e) => { e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='225' fill='%2318181b'%3E%3Crect width='400' height='225'/%3E%3Ctext x='50%25' y='50%25' fill='%2352525b' text-anchor='middle' dominant-baseline='middle' font-size='14'%3ENo Preview%3C/text%3E%3C/svg%3E"; }} />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                  {item.media_type === "video" && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-16 h-16 bg-neon-green/20 backdrop-blur-sm flex items-center justify-center group-hover:bg-neon-green/30 transition-colors">
                        <Play className="w-8 h-8 text-neon-green" fill="currentColor" />
                      </div>
                    </div>
                  )}
                  <Badge className="absolute top-4 left-4 bg-black/60 text-white border-0">
                    {item.media_type === "video" ? <><Video className="w-3 h-3 mr-1" /> Video</> : <><Image className="w-3 h-3 mr-1" /> Photo</>}
                  </Badge>
                  {item.submitted_by_name && (
                    <Badge className="absolute top-4 right-4 bg-neon-cyan/20 text-neon-cyan border-0 text-xs">
                      by {item.submitted_by_name}
                    </Badge>
                  )}
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <p className="font-medium truncate mb-1">{item.title}</p>
                    <div className="flex items-center gap-4 text-xs text-zinc-500">
                      <span className="flex items-center gap-1"><Eye className="w-3 h-3" />{item.views?.toLocaleString()}</span>
                      <LikeButton itemType="car" itemId={item.media_id} size="sm" />
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {/* Media Detail Dialog */}
        <Dialog open={!!selectedMedia} onOpenChange={(open) => { if (!open) setSelectedMedia(null); }}>
          <DialogContent className="bg-zinc-900 border-white/10 max-w-4xl p-0 max-h-[90vh] overflow-y-auto">
            {selectedMedia && (
              <div>
                {selectedMedia.media_type === "video" ? (
                  <video src={resolveUrl(selectedMedia.media_url)} controls className="w-full aspect-video" poster={resolveUrl(selectedMedia.thumbnail_url)} />
                ) : (
                  <img src={resolveUrl(selectedMedia.media_url)} alt={selectedMedia.title} className="w-full" />
                )}
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="font-heading text-xl uppercase tracking-wide">{selectedMedia.title}</h3>
                      <p className="text-zinc-400 text-sm mt-1">{selectedMedia.description}</p>
                      {selectedMedia.submitted_by_name && (
                        <p className="text-xs text-neon-cyan mt-1">Submitted by {selectedMedia.submitted_by_name}</p>
                      )}
                    </div>
                    <LikeButton itemType="car" itemId={selectedMedia.media_id} />
                  </div>
                  {selectedMedia.tags?.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-6">
                      {selectedMedia.tags.map((tag) => (
                        <span key={tag} className="text-xs text-zinc-500 bg-white/5 px-2 py-1">#{tag}</span>
                      ))}
                    </div>
                  )}

                  {/* Comments Section */}
                  <div className="border-t border-white/10 pt-4">
                    <h4 className="text-sm uppercase tracking-wide text-zinc-400 mb-4 flex items-center gap-2">
                      <MessageCircle className="w-4 h-4" /> Comments ({comments.length})
                    </h4>
                    {user && (
                      <div className="flex gap-2 mb-4">
                        <Input
                          value={newComment}
                          onChange={(e) => setNewComment(e.target.value)}
                          placeholder="Add a comment..."
                          className="bg-black/50 border-white/10"
                          onKeyDown={(e) => e.key === "Enter" && handlePostComment()}
                          data-testid="comment-input"
                        />
                        <Button onClick={handlePostComment} size="icon" className="bg-neon-cyan text-black flex-shrink-0" data-testid="post-comment-btn">
                          <Send className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                    {loadingComments ? (
                      <div className="text-center py-4"><Loader2 className="w-5 h-5 animate-spin text-zinc-500 mx-auto" /></div>
                    ) : comments.length === 0 ? (
                      <p className="text-zinc-600 text-sm text-center py-4">No comments yet. Be the first!</p>
                    ) : (
                      <div className="space-y-3 max-h-64 overflow-y-auto">
                        {comments.map((cmt) => (
                          <div key={cmt.comment_id} className="flex gap-3 group" data-testid={`comment-${cmt.comment_id}`}>
                            <div className="w-8 h-8 bg-zinc-800 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold text-neon-cyan">
                              {cmt.user_name?.charAt(0)?.toUpperCase() || "?"}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-medium">{cmt.user_name}</span>
                                <span className="text-xs text-zinc-600">{new Date(cmt.created_at).toLocaleDateString()}</span>
                              </div>
                              <p className="text-sm text-zinc-400 mt-0.5">{cmt.content}</p>
                            </div>
                            {(user?.user_id === cmt.user_id || user?.is_admin) && (
                              <button onClick={() => handleDeleteComment(cmt.comment_id)}
                                className="text-zinc-600 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity">
                                <Trash2 className="w-3 h-3" />
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Coming Soon */}
        <div className="mt-20 glass p-8 md:p-12 text-center">
          <Car className="w-16 h-16 text-neon-green mx-auto mb-6" />
          <h2 className="font-heading text-2xl md:text-3xl uppercase tracking-tight mb-4">Share Your Ride</h2>
          <p className="text-zinc-400 max-w-lg mx-auto">
            {user ? "Hit 'Submit Your Ride' to share your car content with the community!" : "Sign in to submit your car photos and videos!"}
          </p>
        </div>
      </div>
    </div>
  );
};

export default GaragePage;
